"use client";

import React, { useEffect } from "react";
import {
  BlogDetails,
  BlogContent,
  BlogAuthor,
  BlogConclusion
} from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { renderRichText } from "@/lib/richText";
import Image from "next/image";
import VideoContent from "@/components/VideoContent";

interface AuthorImage {
  filename: string;
  alt?: string;
}

interface AuthorContent {
  image: AuthorImage;
  title: string;
}

interface ExtendedBlogAuthor extends BlogAuthor {
  content: AuthorContent;
  name: string;
}

interface BlogIntroProps {
  blok?: BlogDetails & SbBlokData;
  blog_content?: BlogContent[] & SbBlokData;
  blog_author?: ExtendedBlogAuthor[] & SbBlokData;
  blog_conclusion?: BlogConclusion & SbBlokData;
}

const generateTableOfContents = (
  blog_content: (BlogContent[]) | never[],
  blog_conclusion?: (BlogConclusion)[] | undefined,
) => {
  const tocItems: Array<{ title: string; id: string }> = [];

  blog_content?.forEach((section) => {
    if (section.lead) {
      tocItems.push({
        title: section.lead,
        id: section.lead.replace(/\s+/g, "-").toLowerCase()
      });
    }

    if (section.content?.content) {
      section.content.content.forEach((item: any) => {
        if (item.type === "blok" && item.attrs?.body) {
          item.attrs.body.forEach((block: any) => {
            if (block.title && (
              block.component === "different_type_section" ||
              block.component === "feature" ||
              block.component === "faq_section" ||
              block.component === "penetration_testing_section"
            )) {
              tocItems.push({
                title: block.title as string,
                id: (block.title as string).replace(/\s+/g, "-").toLowerCase()
              });
            }
          });
        }
      });
    }
  });

  const uniqueTocItems = tocItems.filter((item, index, self) =>
    index === self.findIndex((t) => t.id === item.id)
  );

  if (Array.isArray(blog_conclusion) && blog_conclusion.length > 0 && blog_conclusion[0]?.title) {
    const conclusionTitle = blog_conclusion[0].title;
    const conclusionId = conclusionTitle
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    return [...uniqueTocItems, {
      title: conclusionTitle,
      id: conclusionId
    }];
  }

  return uniqueTocItems;
};

const BlogIntroComponent: React.FC<BlogIntroProps> = ({ blok, blog_content, blog_author, blog_conclusion }) => {
  const firstAuthor = Array.isArray(blog_author) && blog_author.length > 0 ? blog_author[0] : null;

  useEffect(() => {
    let tooltips: any[] = [];

    const initTooltips = async () => {
      if (typeof window !== "undefined") {
        try {
          const bootstrap = (await import("bootstrap")).default || (await import("bootstrap"));

          if (bootstrap?.Tooltip) {
            const tooltipTriggerList = document.querySelectorAll("[data-bs-toggle='tooltip']");

            tooltips = Array.from(tooltipTriggerList).map(
              (tooltipTriggerEl) =>
                new bootstrap.Tooltip(tooltipTriggerEl, {
                  trigger: "hover",
                  placement: "top",
                })
            );
          } else {
            console.warn("Bootstrap Tooltip is not available.");
          }
        } catch (error) {
          console.error("Error initializing tooltips:", error);
        }
      }
    };

    initTooltips();

    return () => {
      tooltips.forEach((tooltip) => {
        if (tooltip && typeof tooltip.dispose === "function") {
          tooltip.dispose();
        }
      });
    };
  }, []);

  const renderContent = (content: any) => {
    if (!content) return null;

    const hasVideoContent = content.content?.some((item: any) =>
      item.type === "blok" &&
      item.attrs?.body?.some((block: any) => block.component === "video_content")
    );

    if (!hasVideoContent) {
      return <div dangerouslySetInnerHTML={{ __html: renderRichText(content) }} />;
    }

    return (
      <>
        {content.content.map((item: any, index: number) => {
          if (item.type === "blok" && item.attrs?.body) {
            return item.attrs.body.map((block: any) => {
              if (block.component === "video_content") {
                return <VideoContent key={block._uid} blok={block} />;
              }
              return null;
            });
          }
          if (item.type === "paragraph") {
            return (
              <div
                key={index}
                dangerouslySetInnerHTML={{
                  __html: renderRichText({ type: "doc", content: [item] })
                }}
              />
            );
          }
          return null;
        })}
      </>
    );
  };

  if (!blok) return null;

  const { title, description, slug, services, blog_type, date } = blok;

  const socialShareUrl = `/blog/${slug}`;
  const socialTitle = encodeURIComponent(title || "");

  const formattedDate = date
    ? new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    : null;

  if (blog_type === "legacy") {
    return (
      <div className="bg-light py-3">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-18 col-xl-16 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                {firstAuthor?.content?.image?.filename && (
                  <Image
                    src={firstAuthor.content.image.filename}
                    alt={`${firstAuthor.name} Headshot`}
                    className="rounded-circle"
                    width={50}
                    height={50}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    loading="lazy"
                    quality={75}
                  />
                )}
                <div className="ms-3">
                  <h5 className="text-secondary me-2 mb-0">
                    <a className="text-decoration-none" href="#author-bio">
                      {firstAuthor?.name || "Author"}
                    </a>
                  </h5>
                  <span className="blog-author-title text-dark">
                    {firstAuthor?.content?.title || ""}
                  </span>
                </div>
              </div>
              {formattedDate && (
                <div className="blog-author-meta ms-2">
                  <i className="fas fa-calendar me-2" aria-hidden="true"></i>
                  {formattedDate}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...storyblokEditable(blok as SbBlokData)} className="bg-light border-bottom py-4 py-xl-5">
      <div className="container">
        <div className="row g-5">
          <div className="col-24 col-lg-16 col-xl-18" id="blog">
            <h2 className="h3">{title}</h2>

            {description && (
              <div className="lead blog-description">
                {renderContent(description)}
              </div>
            )}

            <div className="blog-share mt-4">
              <h6 className="pretitle text-muted">Share this Article</h6>
              <ul className="component component-social component-social-share list-unstyled list-inline mb-0 d-inline-block text-nowrap">
                {/* Social share buttons... */}
                <li>
                  <a
                    target="_blank"
                    rel="noopener"
                    data-bs-toggle="tooltip"
                    title="Share on Facebook"
                    className="me-1 social-share-link"
                    href={`https://www.facebook.com/sharer.php?u=${socialShareUrl}`}
                    aria-label="Share this on Facebook"
                  >
                    <i className="fab fa-facebook-f fa-lg link-facebook" aria-hidden="true"></i>
                  </a>
                </li>
                <li>
                  <a
                    target="_blank"
                    rel="noopener"
                    data-bs-toggle="tooltip"
                    title="Share on LinkedIn"
                    className="me-1 social-share-link"
                    href={`https://www.linkedin.com/shareArticle?url=${socialShareUrl}&title=${socialTitle}&mini=true`}
                    aria-label="Share this on LinkedIn"
                  >
                    <i className="fab fa-linkedin fa-lg link-linkedin" aria-hidden="true"></i>
                  </a>
                </li>
                <li>
                  <a
                    target="_blank"
                    rel="noopener"
                    data-bs-toggle="tooltip"
                    title="Share on X"
                    className="me-1 social-share-link"
                    href={`https://x.com/share?url=${socialShareUrl}&text=${socialTitle}`}
                    aria-label="Share this on X"
                  >
                    <i className="fab fa-x-twitter fa-lg link-twitter" aria-hidden="true"></i>
                  </a>
                </li>
                <li>
                  <a
                    target="_blank"
                    rel="noopener"
                    data-bs-toggle="tooltip"
                    title="Share via WhatsApp"
                    className="me-1 social-share-link"
                    href={`https://api.whatsapp.com/send?text=${socialTitle} ${socialShareUrl}`}
                    aria-label="Share this via Whatsapp"
                  >
                    <i className="fab fa-whatsapp fa-lg link-whatsapp" aria-hidden="true"></i>
                  </a>
                </li>
                <li>
                  <a
                    data-bs-toggle="tooltip"
                    title="Share via Email"
                    className="me-1"
                    href={`mailto:?subject=${socialTitle}&body=I wanted to share this page with you: ${socialShareUrl}`}
                    aria-label="Share this via Email"
                  >
                    <i className="fa fa-envelope fa-lg link-email" aria-hidden="true"></i>
                  </a>
                </li>
                <li>
                  <a
                    data-bs-toggle="tooltip"
                    title="Copy Link"
                    data-clipboard-text={socialShareUrl}
                    className="me-1 copy-text"
                    href="#"
                    aria-label="Copy a link to this page"
                  >
                    <i className="fa fa-link fa-lg link-copy" aria-hidden="true"></i>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="col-24 col-lg-8 col-xl-6">
            <h6 className="pretitle">Contents</h6>
            <ol className="list-unstyled list-toc mb-0">
              {generateTableOfContents(
                blog_content || [],
                blog_conclusion ? [blog_conclusion] : undefined
              ).map((item, idx) => (
                <li key={idx}>
                  <a href={`#${item.id}`}>{item.title}</a>
                </li>
              ))}
            </ol>
            {services && services !== "Untitled" && (
              <>
                <h6 className="pretitle mt-3">Related Service</h6>
                <a href={`/${services.toLowerCase().replace(/\s+/g, "-")}`}>
                  {services}
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogIntroComponent;